import { classToStyle } from "../../helpers/class-to-style";
import { getClassTokensFromProps } from "./ui/utils/class-tokens";
import { getSlugNodeClassName } from "./slug-node-class";

const breakpointMedia = {
    mobile: "(max-width: 640px)",
    tablet: "(max-width: 768px)",
};

const breakpointOrder = ["base", "tablet", "mobile"];

const containerClassProps = [
    "display",
    "flexDirection",
    "align",
    "alignContent",
    "justify",
    "wrap",
    "rowGap",
    "columnGap",
    "textAlign",
    "width",
    "maxWidth",
    "minWidth",
    "height",
    "minHeight",
    "paddingTop",
    "paddingRight",
    "paddingBottom",
    "paddingLeft",
    "marginTop",
    "marginRight",
    "marginBottom",
    "marginLeft",
    "borderType",
    "borderRadius",
    "position",
    "backgroundColor",
    "backgroundImageSize",
    "backgroundImagePosition",
    "backgroundImageRepeat",
    "zIndex",
    "overflow",
    "boxSizing",
    "flexGrow",
    "flexShrink",
    "flexBasis",
    "alignSelf",
    "order",
];

const buttonClassProps = [
    "width",
    "paddingTop",
    "paddingRight",
    "paddingBottom",
    "paddingLeft",
    "marginTop",
    "marginBottom",
    "marginLeft",
    "marginRight",
    "position",
    "borderRadius",
    "borderColor",
    "borderWidth",
    "borderType",
    "justifyContent",
    "backgroundColor",
    "color",
    "zIndex",
];

const typographyClassProps = [
    "color",
    "fontFamily",
    "fontSize",
    "fontWeight",
    "align",
    "paddingTop",
    "paddingRight",
    "paddingBottom",
    "paddingLeft",
    "marginTop",
    "marginRight",
    "marginBottom",
    "marginLeft",
    "whitespace",
];

const wrapperClassProps = [
    "width",
    "maxWidth",
    "paddingTop",
    "paddingBottom",
    "paddingRight",
    "paddingLeft",
    "marginTop",
    "marginBottom",
    "marginRight",
    "marginLeft",
];

const cardWrapperClassProps = [
    "width",
    "maxWidth",
    "marginTop",
    "marginRight",
    "marginBottom",
    "marginLeft",
];

const cardContentClassProps = [
    "paddingTop",
    "paddingRight",
    "paddingBottom",
    "paddingLeft",
    "borderRadius",
    "shadow",
    "backgroundColor",
    "textAlign",
];

const iconWrapperClassProps = [
    "width",
    "height",
    "paddingTop",
    "paddingBottom",
    "paddingRight",
    "paddingLeft",
    "marginTop",
    "marginBottom",
    "marginRight",
    "marginLeft",
    "color",
    "backgroundColor",
    "display",
    "justify",
    "align",
    "borderRadius",
    "borderColor",
    "borderWidth",
    "borderType",
];

const imageWrapperClassProps = [
    "width",
    "height",
    "paddingTop",
    "paddingRight",
    "paddingBottom",
    "paddingLeft",
    "marginTop",
    "marginBottom",
    "marginLeft",
    "marginRight",
    "position",
    "borderRadius",
    "borderColor",
    "borderWidth",
    "borderType",
    "backgroundColor",
];

const deviderClassProps = [
    "width",
    "maxWidth",
    "height",
    "paddingTop",
    "paddingRight",
    "paddingBottom",
    "paddingLeft",
    "marginTop",
    "marginBottom",
    "marginLeft",
    "marginRight",
    "position",
    "borderRadius",
    "borderColor",
    "borderWidth",
    "borderType",
    "justifyContent",
    "backgroundColor",
];

const runtimeClassProps = [
    "display",
    "direction",
    "align",
    "justify",
    "wrap",
    "alignSelf",
    "rowGap",
    "columnGap",
    "width",
    "tablebWidth",
    "mobileWidth",
    "minHeight",
    "paddingTop",
    "paddingRight",
    "paddingBottom",
    "paddingLeft",
    "marginTop",
    "marginRight",
    "marginBottom",
    "marginLeft",
    "backgroundType",
    "borderType",
    "borderRadius",
    "position",
    "backgroundColor",
    "zIndex",
    "order",
];

const defaultClassProps = [
    ...new Set([
        ...containerClassProps,
        ...buttonClassProps,
        ...typographyClassProps,
        ...cardContentClassProps,
        ...iconWrapperClassProps,
        ...imageWrapperClassProps,
        ...deviderClassProps,
        ...runtimeClassProps,
        "objectFit",
        "shadow",
        "size",
    ]),
];

const componentStyleConfigs = {
    Container: [
        { classProps: containerClassProps, extraStyle: resolveStructuredBackgroundStyles },
    ],
    Button: [
        { classProps: buttonClassProps },
    ],
    Typography: [
        { classProps: typographyClassProps },
    ],
    ElementorAccordions: [
        { classProps: wrapperClassProps },
        { slot: "content", classProps: ["borderRadius", "backgroundColor"] },
    ],
    ElementorCard: [
        { classProps: cardWrapperClassProps },
        { slot: "content", classProps: cardContentClassProps },
    ],
    ElementorDialog: [
        { classProps: wrapperClassProps },
        { slot: "content", classProps: ["size"] },
    ],
    ElementorTabs: [
        { classProps: wrapperClassProps },
    ],
    ElementorSheet: [
        { classProps: ["width"] },
    ],
    ElementorDevider: [
        { classProps: deviderClassProps },
    ],
    ElementorIcon: [
        { classProps: iconWrapperClassProps },
    ],
    Image: [
        { classProps: imageWrapperClassProps },
        { slot: "media", classProps: ["objectFit"] },
    ],
    ImageComponent: [
        { classProps: imageWrapperClassProps },
        { slot: "media", classProps: ["objectFit"] },
    ],
    Runtime: [
        { classProps: runtimeClassProps },
    ],
    FormRuntime: [
        { classProps: [] },
    ],
    Input: [
        { classProps: [] },
    ],
};

const extraTokenStyles = {
    absolute: { position: "absolute" },
    fixed: { position: "fixed" },
    relative: { position: "relative" },
    static: { position: "static" },
    sticky: { position: "sticky" },
    "box-border": { boxSizing: "border-box" },
    "box-content": { boxSizing: "content-box" },
    "bg-auto": { backgroundSize: "auto" },
    "bg-cover": { backgroundSize: "cover" },
    "bg-contain": { backgroundSize: "contain" },
    "bg-bottom": { backgroundPosition: "bottom" },
    "bg-center": { backgroundPosition: "center" },
    "bg-left": { backgroundPosition: "left" },
    "bg-left-bottom": { backgroundPosition: "left bottom" },
    "bg-left-top": { backgroundPosition: "left top" },
    "bg-right": { backgroundPosition: "right" },
    "bg-right-bottom": { backgroundPosition: "right bottom" },
    "bg-right-top": { backgroundPosition: "right top" },
    "bg-top": { backgroundPosition: "top" },
    "bg-no-repeat": { backgroundRepeat: "no-repeat" },
    "bg-repeat": { backgroundRepeat: "repeat" },
    "bg-repeat-x": { backgroundRepeat: "repeat-x" },
    "bg-repeat-y": { backgroundRepeat: "repeat-y" },
};

const backgroundPropNames = [
    "backgroundType",
    "backgroundSolidColor",
    "backgroundSolidOpacity",
    "backgroundGradientDirection",
    "backgroundGradientStart",
    "backgroundGradientStartOpacity",
    "backgroundGradientEnd",
    "backgroundGradientEndOpacity",
    "backgroundImageUrl",
    "backgroundImageFallbackColor",
    "backgroundImageFallbackOpacity",
    "backgroundOverlayEnabled",
    "backgroundOverlayDirection",
    "backgroundOverlayStart",
    "backgroundOverlayStartOpacity",
    "backgroundOverlayEnd",
    "backgroundOverlayEndOpacity",
];

function splitVariants(token) {
    let bracketDepth = 0;
    let parenDepth = 0;
    let quote = null;
    let lastVariantColon = -1;

    for (let i = 0; i < token.length; i += 1) {
        const char = token[i];

        if (quote) {
            if (char === quote && token[i - 1] !== "\\") {
                quote = null;
            }
            continue;
        }

        if (char === "'" || char === '"') {
            quote = char;
            continue;
        }

        if (char === "[") bracketDepth += 1;
        if (char === "]") bracketDepth = Math.max(0, bracketDepth - 1);
        if (char === "(") parenDepth += 1;
        if (char === ")") parenDepth = Math.max(0, parenDepth - 1);

        if (char === ":" && bracketDepth === 0 && parenDepth === 0) {
            lastVariantColon = i;
        }
    }

    if (lastVariantColon < 0) {
        return { baseToken: token, variants: [] };
    }

    return {
        baseToken: token.slice(lastVariantColon + 1),
        variants: token.slice(0, lastVariantColon).split(":"),
    };
}

function toCssProperty(property) {
    return property.replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`);
}

function isSafeCssValue(value) {
    return typeof value !== "string" || !/[;{}]/.test(value);
}

function styleToCss(style) {
    return Object.entries(style)
        .filter(([, value]) => value !== null && value !== undefined && value !== "" && isSafeCssValue(value))
        .map(([property, value]) => `${toCssProperty(property)}:${value}`)
        .join(";");
}

function hexToRgbString(hex) {
    if (!hex || typeof hex !== "string") return null;
    const normalized = hex.trim().replace("#", "");
    if (![3, 6].includes(normalized.length)) return null;
    const full = normalized.length === 3
        ? normalized.split("").map((char) => `${char}${char}`).join("")
        : normalized;
    const int = Number.parseInt(full, 16);
    if (Number.isNaN(int)) return null;
    const r = (int >> 16) & 255;
    const g = (int >> 8) & 255;
    const b = int & 255;
    return `${r}, ${g}, ${b}`;
}

function rgbaColor(hex, opacity = 1) {
    const rgb = hexToRgbString(hex);
    if (!rgb) return null;
    return `rgba(${rgb}, ${opacity})`;
}

function cssUrl(value) {
    if (!value || typeof value !== "string" || /[;{}\n\r]/.test(value)) {
        return null;
    }

    const escaped = value.trim().replace(/\\/g, "\\\\").replace(/"/g, '\\"');
    return escaped ? `url("${escaped}")` : null;
}

function resolveStructuredBackgroundStyles(props) {
    const backgroundType = props.backgroundType ?? "none";

    if (!["none", "color", "gradient", "image"].includes(backgroundType)) {
        return {};
    }

    if (backgroundType === "none") {
        return {
            backgroundImage: "none",
            backgroundColor: "transparent",
        };
    }

    if (backgroundType === "color") {
        const color = rgbaColor(props.backgroundSolidColor || "#ffffff", props.backgroundSolidOpacity ?? 1);
        return color ? { backgroundColor: color, backgroundImage: "none" } : {};
    }

    if (backgroundType === "gradient") {
        const start = rgbaColor(props.backgroundGradientStart || "#274067", props.backgroundGradientStartOpacity ?? 1);
        const end = rgbaColor(props.backgroundGradientEnd || "#000000", props.backgroundGradientEndOpacity ?? 1);

        if (!start || !end) return {};

        return {
            backgroundImage: `linear-gradient(${props.backgroundGradientDirection || "to bottom"}, ${start}, ${end})`,
            backgroundColor: "transparent",
        };
    }

    const layers = [];
    const imageUrl = cssUrl(props.backgroundImageUrl);
    const overlayEnabled = props.backgroundOverlayEnabled ?? false;

    if (overlayEnabled) {
        const overlayStart = rgbaColor(props.backgroundOverlayStart || "#000000", props.backgroundOverlayStartOpacity ?? 0.4);
        const overlayEnd = rgbaColor(props.backgroundOverlayEnd || "#000000", props.backgroundOverlayEndOpacity ?? 0.4);

        if (overlayStart && overlayEnd) {
            layers.push(`linear-gradient(${props.backgroundOverlayDirection || "to bottom"}, ${overlayStart}, ${overlayEnd})`);
        }
    }

    if (imageUrl) {
        layers.push(imageUrl);
    }

    return {
        backgroundImage: layers.length ? layers.join(", ") : "none",
        backgroundColor: rgbaColor(props.backgroundImageFallbackColor || "#ffffff", props.backgroundImageFallbackOpacity ?? 1) || "transparent",
    };
}

function getBaseProps(props) {
    const baseProps = { ...(props || {}) };
    delete baseProps.responsive;
    return baseProps;
}

function getComponentTypeName(node) {
    return node?.type?.resolvedName || node?.displayName || "";
}

function getStyleConfigs(node) {
    return componentStyleConfigs[getComponentTypeName(node)] || [
        { classProps: defaultClassProps },
    ];
}

function propsToStyle(props, classProps) {
    const tokens = getClassTokensFromProps(props, classProps);
    return classToStyle(tokens);
}

function hasAnyProp(props, propNames) {
    if (!props || typeof props !== "object") return false;
    return propNames.some((propName) => propName in props);
}

function buildSlotStyle(config, baseProps, overrideProps, breakpoint) {
    const props = breakpoint === "base"
        ? baseProps
        : overrideProps;
    const style = propsToStyle(props, config.classProps);

    if (typeof config.extraStyle === "function") {
        const shouldApplyExtraStyle = breakpoint === "base"
            || hasAnyProp(overrideProps, backgroundPropNames);

        if (shouldApplyExtraStyle) {
            Object.assign(style, config.extraStyle({ ...baseProps, ...overrideProps }));
        }
    }

    return style;
}

function buildRule(nodeId, slot, style) {
    const declarations = styleToCss(style);
    if (!declarations) return "";

    return `.${getSlugNodeClassName(nodeId, slot)}{${declarations}}`;
}

function collectNodeRules(nodeId, node) {
    if (!node?.props || typeof node.props !== "object") return [];

    const baseProps = getBaseProps(node.props);
    const responsive = node.props.responsive && typeof node.props.responsive === "object"
        ? node.props.responsive
        : {};
    const rules = [];

    getStyleConfigs(node).forEach((config) => {
        const slot = config.slot || "root";
        const baseStyle = buildSlotStyle(config, baseProps, {}, "base");
        const baseRule = buildRule(nodeId, slot, baseStyle);

        if (baseRule) {
            rules.push({ breakpoint: "base", css: baseRule });
        }

        Object.keys(breakpointMedia).forEach((breakpoint) => {
            const overrideProps = responsive[breakpoint];

            if (!overrideProps || typeof overrideProps !== "object") return;

            const style = buildSlotStyle(config, baseProps, overrideProps, breakpoint);
            const rule = buildRule(nodeId, slot, style);

            if (rule) {
                rules.push({ breakpoint, css: rule });
            }
        });
    });

    return rules;
}

export function buildSlugDynamicCss(serializedBuilderData) {
    if (!serializedBuilderData || typeof serializedBuilderData !== "object") {
        return "";
    }

    const ruleBuckets = {
        base: [],
        mobile: [],
        tablet: [],
    };

    Object.entries(serializedBuilderData).forEach(([nodeId, node]) => {
        collectNodeRules(nodeId, node).forEach((rule) => {
            ruleBuckets[rule.breakpoint].push(rule.css);
        });
    });

    return breakpointOrder
        .map((breakpoint) => {
            const rules = ruleBuckets[breakpoint];
            if (!rules.length) return "";

            if (breakpoint === "base") {
                return rules.join("\n");
            }

            return `@media ${breakpointMedia[breakpoint]}{\n${rules.join("\n")}\n}`;
        })
        .filter(Boolean)
        .join("\n");
}
